import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Form,
  Grid,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Segment,
  Select,
  Table,
  Tab,
} from 'semantic-ui-react';
import { API, showError, showSuccess, isRoot } from '../helpers';

const ChannelPricingOverride = () => {
  const { t, i18n } = useTranslation();
  const [channels, setChannels] = useState([]);
  const [selectedChannelId, setSelectedChannelId] = useState('');
  const [pricingList, setPricingList] = useState([]);
  const [groupRatioList, setGroupRatioList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [groupRatioModalOpen, setGroupRatioModalOpen] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [editingGroupRatio, setEditingGroupRatio] = useState(null);
  
  // 权限判断：只有root用户可以编辑/删除
  const canEdit = isRoot();
  
  // 获取当前语言环境
  const isEnglish = i18n.language === 'en';
  const currencySymbol = isEnglish ? '$' : '¥';
  
  const [formData, setFormData] = useState({
    channel_id: '',
    model_name: '',
    prompt_price: '',
    completion_price: '',
    model_ratio: '',
    completion_ratio: '',
    billing_type: 'per_token', // per_token=按量付费, per_call=按次付费
    per_call_price: '',
    currency_symbol: currencySymbol, // 根据当前语言动态设置
  });
  const [groupRatioFormData, setGroupRatioFormData] = useState({
    channel_id: '',
    group_name: '',
    ratio: '',
  });

  // 获取所有渠道列表
  const fetchChannels = async () => {
    try {
      const res = await API.get('/api/channel/?all=1');
      const { success, data, message } = res.data;
      if (success) {
        // 过滤掉君云AIGC渠道（类型15），因为它们走独立的计费表
        const filteredChannels = data.filter(ch => ch.type !== 15);
        setChannels(filteredChannels.map(ch => ({
          key: ch.id,
          value: ch.id,
          text: `${ch.name} (ID: ${ch.id})`,
        })));
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.fetch_channels_failed'));
    }
  };

  // 获取指定渠道的定价列表
  const fetchPricingList = async (channelId) => {
    if (!channelId) return;
    setLoading(true);
    try {
      const res = await API.get(`/api/channel-pricing-override/${channelId}`);
      const { success, data, message } = res.data;
      if (success) {
        setPricingList(data || []);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.fetch_pricing_failed'));
    }
    setLoading(false);
  };

  // 获取指定渠道的分组倍率列表
  const fetchGroupRatioList = async (channelId) => {
    if (!channelId) return;
    setLoading(true);
    try {
      const res = await API.get(`/api/channel-pricing-override/${channelId}/group-ratios`);
      const { success, data, message } = res.data;
      if (success) {
        setGroupRatioList(data || []);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.fetch_group_ratio_failed'));
    }
    setLoading(false);
  };

  // 初始化时加载渠道列表
  useEffect(() => {
    fetchChannels();
  }, []);

  // 渠道改变时重新加载数据
  useEffect(() => {
    if (selectedChannelId) {
      fetchPricingList(selectedChannelId);
      fetchGroupRatioList(selectedChannelId);
    } else {
      setPricingList([]);
      setGroupRatioList([]);
    }
  }, [selectedChannelId]);

  // 打开定价新增/编辑弹窗
  const openModal = (pricing = null) => {
    if (pricing) {
      setEditingPricing(pricing);
      // 后端返回的 pricing 已经是根据语言环境转换过的显示价格
      const promptPriceDisplay = pricing.prompt_price > 0 
        ? pricing.prompt_price.toFixed(4) 
        : '';
      const completionPriceDisplay = pricing.completion_price > 0 
        ? pricing.completion_price.toFixed(4) 
        : '';
      const perCallPriceDisplay = pricing.per_call_price > 0 
        ? pricing.per_call_price.toFixed(6) 
        : '';
      setFormData({
        channel_id: pricing.channel_id,
        model_name: pricing.model_name,
        prompt_price: promptPriceDisplay,
        completion_price: completionPriceDisplay,
        model_ratio: pricing.model_ratio || '',
        completion_ratio: pricing.completion_ratio || '',
        billing_type: pricing.billing_type || 'per_token',
        per_call_price: perCallPriceDisplay,
        currency_symbol: pricing.currency_symbol || currencySymbol,
      });
    } else {
      setEditingPricing(null);
      setFormData({
        channel_id: selectedChannelId,
        model_name: '',
        prompt_price: '',
        completion_price: '',
        model_ratio: '',
        completion_ratio: '',
        billing_type: 'per_token',
        per_call_price: '',
        currency_symbol: currencySymbol,
      });
    }
    setModalOpen(true);
  };

  // 打开分组倍率新增/编辑弹窗
  const openGroupRatioModal = (ratio = null) => {
    if (ratio) {
      setEditingGroupRatio(ratio);
      setGroupRatioFormData({
        channel_id: ratio.channel_id,
        group_name: ratio.group_name,
        ratio: ratio.ratio || '',
      });
    } else {
      setEditingGroupRatio(null);
      setGroupRatioFormData({
        channel_id: selectedChannelId,
        group_name: '',
        ratio: '',
      });
    }
    setGroupRatioModalOpen(true);
  };

  // 关闭弹窗
  const closeModal = () => {
    setModalOpen(false);
    setEditingPricing(null);
    setFormData({
      channel_id: '',
      model_name: '',
      prompt_price: '',
      completion_price: '',
      model_ratio: '',
      completion_ratio: '',
      billing_type: 'per_token',
      per_call_price: '',
      currency_symbol: currencySymbol,
    });
  };

  // 关闭分组倍率弹窗
  const closeGroupRatioModal = () => {
    setGroupRatioModalOpen(false);
    setEditingGroupRatio(null);
    setGroupRatioFormData({
      channel_id: '',
      group_name: '',
      ratio: '',
    });
  };

  // 保存定价
  const savePricing = async () => {
    if (!formData.model_name.trim()) {
      showError(t('pricing.messages.model_name_required'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        channel_id: parseInt(selectedChannelId),
        model_name: formData.model_name.trim(),
        prompt_price: parseFloat(formData.prompt_price) || 0,
        completion_price: parseFloat(formData.completion_price) || 0,
        model_ratio: parseFloat(formData.model_ratio) || 0,
        completion_ratio: parseFloat(formData.completion_ratio) || 0,
        billing_type: formData.billing_type || 'per_token',
        per_call_price: parseFloat(formData.per_call_price) || 0,
        is_enabled: 1,
      };

      const res = await API.post('/api/channel-pricing-override/', payload);
      const { success, message } = res.data;
      if (success) {
        showSuccess(editingPricing ? t('pricing.messages.pricing_updated') : t('pricing.messages.pricing_created'));
        closeModal();
        fetchPricingList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.save_pricing_failed'));
    }
    setLoading(false);
  };

  // 保存分组倍率
  const saveGroupRatio = async () => {
    if (!groupRatioFormData.group_name.trim()) {
      showError(t('pricing.messages.group_name_required'));
      return;
    }

    setLoading(true);
    try {
      const payload = {
        channel_id: parseInt(selectedChannelId),
        group_name: groupRatioFormData.group_name.trim(),
        ratio: parseFloat(groupRatioFormData.ratio) || 1,
        is_enabled: 1,
      };

      const res = await API.post('/api/channel-pricing-override/group-ratio', payload);
      const { success, message } = res.data;
      if (success) {
        showSuccess(editingGroupRatio ? t('pricing.messages.group_ratio_updated') : t('pricing.messages.group_ratio_created'));
        closeGroupRatioModal();
        fetchGroupRatioList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.save_group_ratio_failed'));
    }
    setLoading(false);
  };

  // 删除定价
  const deletePricing = async (pricing) => {
    if (!window.confirm(t('pricing.messages.confirm_delete_pricing', { model: pricing.model_name }))) {
      return;
    }

    setLoading(true);
    try {
      const res = await API.delete(`/api/channel-pricing-override/${pricing.channel_id}/${pricing.model_name}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('pricing.messages.pricing_deleted'));
        fetchPricingList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.delete_pricing_failed'));
    }
    setLoading(false);
  };

  // 删除分组倍率
  const deleteGroupRatio = async (ratio) => {
    if (!window.confirm(t('pricing.messages.confirm_delete_group_ratio', { group: ratio.group_name }))) {
      return;
    }

    setLoading(true);
    try {
      const res = await API.delete(`/api/channel-pricing-override/${ratio.channel_id}/group-ratio/${ratio.group_name}`);
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('pricing.messages.group_ratio_deleted'));
        fetchGroupRatioList(selectedChannelId);
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.delete_group_ratio_failed'));
    }
    setLoading(false);
  };

  // 重新加载配置到内存
  const reloadPricing = async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/channel-pricing-override/reload');
      const { success, message } = res.data;
      if (success) {
        showSuccess(t('pricing.messages.config_reloaded'));
      } else {
        showError(message);
      }
    } catch (err) {
      showError(t('pricing.messages.reload_failed'));
    }
    setLoading(false);
  };

  // 模型定价表格
  const PricingTable = () => (
    <>
      {canEdit && (
        <Button
          primary
          icon
          labelPosition='left'
          onClick={() => openModal()}
          disabled={!selectedChannelId}
          loading={loading}
          style={{ marginBottom: '1em' }}
        >
          <Icon name='plus' />
          {t('pricing.buttons.add_pricing')}
        </Button>
      )}

      {selectedChannelId && pricingList.length === 0 && !loading && (
        <Message warning>
          <Message.Header>{t('pricing.messages.no_pricing')}</Message.Header>
          <p>{t('pricing.messages.no_pricing_desc')}</p>
        </Message>
      )}

      {selectedChannelId && pricingList.length > 0 && (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('pricing.col.model_name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.billing_type')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.prompt_price')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.completion_price')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.per_call_price')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.model_ratio')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.completion_ratio')}</Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>{t('pricing.col.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {pricingList.map((pricing) => (
              <Table.Row key={pricing.id}>
                <Table.Cell>
                  <Label>{pricing.model_name}</Label>
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_call' 
                    ? <Label color='orange'>{t('pricing.billing_type.per_call')}</Label>
                    : <Label color='blue'>{t('pricing.billing_type.per_token')}</Label>}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_token' && pricing.prompt_price > 0 
                    ? `${pricing.currency_symbol}${pricing.prompt_price.toFixed(4)} / 1M`
                    : '-'}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_token' && pricing.completion_price > 0 
                    ? `${pricing.currency_symbol}${pricing.completion_price.toFixed(4)} / 1M`
                    : '-'}
                </Table.Cell>
                <Table.Cell>
                  {pricing.billing_type === 'per_call' && pricing.per_call_price > 0 
                    ? `${pricing.currency_symbol}${pricing.per_call_price.toFixed(6)} / 次`
                    : '-'}
                </Table.Cell>
                <Table.Cell>{pricing.model_ratio > 0 ? pricing.model_ratio : '-'}</Table.Cell>
                <Table.Cell>{pricing.completion_ratio > 0 ? pricing.completion_ratio : '-'}</Table.Cell>
                <Table.Cell textAlign='center'>
                  {canEdit && (
                    <Button
                      icon
                      size='mini'
                      onClick={() => openModal(pricing)}
                      title={t('pricing.buttons.edit')}
                    >
                      <Icon name='edit' />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      icon
                      size='mini'
                      color='red'
                      onClick={() => deletePricing(pricing)}
                      title={t('pricing.buttons.delete')}
                    >
                      <Icon name='trash' />
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );

  // 分组倍率表格
  const GroupRatioTable = () => (
    <>
      {canEdit && (
        <Button
          primary
          icon
          labelPosition='left'
          onClick={() => openGroupRatioModal()}
          disabled={!selectedChannelId}
          loading={loading}
          style={{ marginBottom: '1em' }}
        >
          <Icon name='plus' />
          {t('pricing.buttons.add_group_ratio')}
        </Button>
      )}

      {selectedChannelId && groupRatioList.length === 0 && !loading && (
        <Message info>
          <Message.Header>{t('pricing.messages.using_global_ratio')}</Message.Header>
          <p>{t('pricing.messages.using_global_ratio_desc1')}</p>
          <p>{t('pricing.messages.using_global_ratio_desc2')}</p>
        </Message>
      )}

      {selectedChannelId && groupRatioList.length > 0 && (
        <Table celled striped selectable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{t('pricing.col.group_name')}</Table.HeaderCell>
              <Table.HeaderCell>{t('pricing.col.ratio')}</Table.HeaderCell>
              <Table.HeaderCell textAlign='center'>{t('pricing.col.actions')}</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {groupRatioList.map((ratio) => (
              <Table.Row key={ratio.id}>
                <Table.Cell>
                  <Label>{ratio.group_name}</Label>
                </Table.Cell>
                <Table.Cell>{ratio.ratio}</Table.Cell>
                <Table.Cell textAlign='center'>
                  {canEdit && (
                    <Button
                      icon
                      size='mini'
                      onClick={() => openGroupRatioModal(ratio)}
                      title='编辑'
                    >
                      <Icon name='edit' />
                    </Button>
                  )}
                  {canEdit && (
                    <Button
                      icon
                      size='mini'
                      color='red'
                      onClick={() => deleteGroupRatio(ratio)}
                      title='删除'
                    >
                      <Icon name='trash' />
                    </Button>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}
    </>
  );

  const panes = [
    {
      menuItem: t('pricing.tabs.pricing'),
      render: () => (
        <Tab.Pane attached={false}>
          <PricingTable />
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('pricing.tabs.group_ratio'),
      render: () => (
        <Tab.Pane attached={false}>
          <GroupRatioTable />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <>
      <Header as='h3'>
        <Icon name='dollar sign' />
        <Header.Content>
          {t('pricing.title')}
          <Header.Subheader>{t('pricing.subtitle')}</Header.Subheader>
        </Header.Content>
      </Header>

      <Segment>
        <Grid columns={2} stackable>
          <Grid.Column width={10}>
            <Form>
              <Form.Field>
                <label>{t('pricing.filter.select_channel')}</label>
                <Select
                  placeholder={t('pricing.filter.select_channel_placeholder')}
                  options={channels}
                  value={selectedChannelId}
                  onChange={(e, { value }) => setSelectedChannelId(value)}
                  search
                  selection
                />
              </Form.Field>
            </Form>
          </Grid.Column>
          <Grid.Column width={6} textAlign='right' verticalAlign='bottom'>
            <Button
              icon
              labelPosition='left'
              onClick={reloadPricing}
              loading={loading}
            >
              <Icon name='refresh' />
              {t('pricing.buttons.reload')}
            </Button>
          </Grid.Column>
        </Grid>

        {!selectedChannelId && (
          <Message info style={{ marginTop: '1em' }}>
            <Message.Header>{t('pricing.messages.tip')}</Message.Header>
            <p>{t('pricing.messages.tip_desc')}</p>
            <p><strong>{t('pricing.messages.tip_note')}:</strong>{t('pricing.messages.tip_note_desc')}</p>
          </Message>
        )}

        {selectedChannelId && (
          <Tab
            menu={{ secondary: true, pointing: true }}
            panes={panes}
            style={{ marginTop: '1em' }}
          />
        )}
      </Segment>

      {/* 定价弹窗 */}
      <Modal open={modalOpen} onClose={closeModal} size='small'>
        <Modal.Header>
          {editingPricing ? t('pricing.modal.edit_title') : t('pricing.modal.add_title')}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>{t('pricing.modal.channel_id')}</label>
              <Input value={selectedChannelId} disabled />
            </Form.Field>
            <Form.Field required>
              <label>{t('pricing.modal.model_name')}</label>
              <Input
                placeholder={t('pricing.modal.model_name_placeholder')}
                value={formData.model_name}
                onChange={(e) => setFormData({ ...formData, model_name: e.target.value })}
                disabled={!!editingPricing}
              />
            </Form.Field>
            <Form.Field required>
              <label>{t('pricing.modal.billing_type')}</label>
              <Select
                options={[
                  { key: 'per_token', text: t('pricing.billing_type.per_token_full'), value: 'per_token' },
                  { key: 'per_call', text: t('pricing.billing_type.per_call_full'), value: 'per_call' },
                ]}
                value={formData.billing_type}
                onChange={(e, { value }) => setFormData({ ...formData, billing_type: value })}
              />
            </Form.Field>

            {/* 按量付费字段 */}
            {formData.billing_type === 'per_token' && (
              <>
                <Form.Field>
                  <label>{t('pricing.modal.prompt_price', { currencySymbol: formData.currency_symbol })}</label>
                  <Input
                    type='number'
                    step='0.0001'
                    placeholder={t('pricing.modal.prompt_price_placeholder')}
                    value={formData.prompt_price}
                    onChange={(e) => {
                      setFormData({ ...formData, prompt_price: e.target.value });
                    }}
                  />
                  <small>{t('pricing.modal.prompt_price_hint', { value: formData.prompt_price || '0' })}</small>
                </Form.Field>
                <Form.Field>
                  <label>{t('pricing.modal.completion_price', { currencySymbol: formData.currency_symbol })}</label>
                  <Input
                    type='number'
                    step='0.0001'
                    placeholder={t('pricing.modal.completion_price_placeholder')}
                    value={formData.completion_price}
                    onChange={(e) => {
                      setFormData({ ...formData, completion_price: e.target.value });
                    }}
                  />
                  <small>{t('pricing.modal.completion_price_hint', { value: formData.completion_price || '0' })}</small>
                </Form.Field>
              </>
            )}

            {/* 按次付费字段 */}
            {formData.billing_type === 'per_call' && (
              <Form.Field>
                <label>{t('pricing.modal.per_call_price', { currencySymbol: formData.currency_symbol })}</label>
                <Input
                  type='number'
                  step='0.000001'
                  placeholder={t('pricing.modal.per_call_price_placeholder')}
                  value={formData.per_call_price}
                  onChange={(e) => {
                    setFormData({ ...formData, per_call_price: e.target.value });
                  }}
                />
                <small>{t('pricing.modal.per_call_price_hint', { value: formData.per_call_price || '0' })}</small>
              </Form.Field>
            )}

            <Form.Field>
              <label>{t('pricing.modal.model_ratio')}</label>
              <Input
                type='number'
                step='0.0001'
                placeholder={t('pricing.modal.model_ratio_placeholder')}
                value={formData.model_ratio}
                onChange={(e) => setFormData({ ...formData, model_ratio: e.target.value })}
              />
              <small>留空或填0表示使用全局配置</small>
            </Form.Field>
            <Form.Field>
              <label>{t('pricing.modal.completion_ratio')}</label>
              <Input
                type='number'
                step='0.0001'
                placeholder={t('pricing.modal.completion_ratio_placeholder')}
                value={formData.completion_ratio}
                onChange={(e) => setFormData({ ...formData, completion_ratio: e.target.value })}
              />
              <small>{t('pricing.modal.ratio_hint')}</small>
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeModal}>{t('pricing.buttons.cancel')}</Button>
          <Button primary onClick={savePricing} loading={loading}>
            {t('pricing.buttons.save')}
          </Button>
        </Modal.Actions>
      </Modal>

      {/* 分组倍率弹窗 */}
      <Modal open={groupRatioModalOpen} onClose={closeGroupRatioModal} size='small'>
        <Modal.Header>
          {editingGroupRatio ? t('pricing.group_ratio.edit_title') : t('pricing.group_ratio.add_title')}
        </Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Field>
              <label>{t('pricing.modal.channel_id')}</label>
              <Input value={selectedChannelId} disabled />
            </Form.Field>
            <Form.Field required>
              <label>{t('pricing.group_ratio.group_name')}</label>
              <Input
                placeholder={t('pricing.group_ratio.group_name_placeholder')}
                value={groupRatioFormData.group_name}
                onChange={(e) => setGroupRatioFormData({ ...groupRatioFormData, group_name: e.target.value })}
                disabled={!!editingGroupRatio}
              />
            </Form.Field>
            <Form.Field required>
              <label>{t('pricing.group_ratio.ratio')}</label>
              <Input
                type='number'
                step='0.0001'
                placeholder={t('pricing.group_ratio.ratio_placeholder')}
                value={groupRatioFormData.ratio}
                onChange={(e) => setGroupRatioFormData({ ...groupRatioFormData, ratio: e.target.value })}
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={closeGroupRatioModal}>{t('pricing.buttons.cancel')}</Button>
          <Button primary onClick={saveGroupRatio} loading={loading}>
            {t('pricing.buttons.save')}
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default ChannelPricingOverride;
